import React, { useState, useEffect, useRef } from 'react';
import { Search, BookOpen, ChevronRight, Download, ArrowUp, Hash, Filter, Truck, Package, Globe, Clock, Zap } from 'lucide-react';
// import SEOHead from '../component/seo/SEOHead';
import Header from '../component/layout/Header';
import Footer from '../component/layout/Footer';

const LogisticsTermsPage = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedLetter, setSelectedLetter] = useState('all');
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 500);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const categories = [
        { id: 'all', name: 'All Terms', icon: BookOpen },
        { id: 'transportation', name: 'Transportation', icon: Truck },
        { id: 'shipping', name: 'Shipping & Freight', icon: Package },
        { id: 'documentation', name: 'Documentation', icon: Hash },
        { id: 'international', name: 'International Trade', icon: Globe },
        { id: 'operations', name: 'Operations', icon: Clock }
    ];

    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

    const logisticsTerms = [
        {
            term: "3PL (Third-Party Logistics)",
            definition: "A company that provides logistics services to other businesses, including transportation, warehousing, and distribution. 3PLs help companies focus on their core business while outsourcing supply chain management.",
            category: "operations",
            letter: "3"
        },
        {
            term: "Accessorial Charges",
            definition: "Additional fees charged by carriers for services beyond standard pickup and delivery, such as liftgate service, inside delivery, residential delivery, or detention time.",
            category: "transportation",
            letter: "A"
        },
        {
            term: "Air Freight",
            definition: "Transportation of goods by aircraft. This is typically the fastest but most expensive mode of transportation, often used for time-sensitive or high-value shipments.",
            category: "transportation",
            letter: "A"
        },
        {
            term: "Bill of Lading (BOL)",
            definition: "A legal document between a shipper and carrier that serves as a receipt for goods, evidence of the contract of carriage, and document of title. It contains details about the cargo, origin, destination, and terms of transport.",
            category: "documentation",
            letter: "B"
        },
        {
            term: "Backhaul",
            definition: "The return trip of a transportation vehicle after delivering cargo, ideally with cargo to avoid empty return trips and reduce transportation costs.",
            category: "transportation",
            letter: "B"
        },
        {
            term: "Bulk Freight",
            definition: "Large quantities of unpackaged cargo such as grains, coal, ore, or liquids that are transported in bulk form without individual containers or packaging.",
            category: "shipping",
            letter: "B"
        },
        {
            term: "Carrier",
            definition: "A company that transports goods or passengers for compensation. This includes trucking companies, airlines, shipping lines, and railroad companies.",
            category: "transportation",
            letter: "C"
        },
        {
            term: "Consignee",
            definition: "The individual or company designated to receive a shipment of goods. The consignee is typically the buyer or recipient of the cargo.",
            category: "shipping",
            letter: "C"
        },
        {
            term: "Consignor",
            definition: "The person or company that ships goods to another party. The consignor is typically the seller or sender of the cargo.",
            category: "shipping",
            letter: "C"
        },
        {
            term: "Container",
            definition: "A standard-sized rectangular box used to transport freight by ship, rail, or highway. International containers are typically 20' or 40' and conform to ISO standards.",
            category: "shipping",
            letter: "C"
        },
        {
            term: "Cross-Docking",
            definition: "A logistics practice where incoming goods are directly transferred from inbound to outbound transportation with minimal or no storage time in between.",
            category: "operations",
            letter: "C"
        },
        {
            term: "Customs Clearance",
            definition: "The process of obtaining official permission to import or export goods across international borders, involving documentation review and duty payment.",
            category: "international",
            letter: "C"
        },
        {
            term: "Deadhead",
            definition: "Operating a transportation vehicle without cargo, typically on a return trip. This represents non-revenue generating miles for carriers.",
            category: "transportation",
            letter: "D"
        },
        {
            term: "Demurrage",
            definition: "A charge imposed when cargo containers or equipment are held beyond the allowed free time at ports or terminals, compensating for the delay in returning equipment.",
            category: "shipping",
            letter: "D"
        },
        {
            term: "Distribution Center",
            definition: "A facility used for the storage and distribution of goods, typically featuring advanced inventory management systems and serving multiple customers or regions.",
            category: "operations",
            letter: "D"
        },
        {
            term: "Door-to-Door",
            definition: "A shipping service where cargo is picked up at the shipper's location and delivered directly to the consignee's location, providing complete transportation service.",
            category: "shipping",
            letter: "D"
        },
        {
            term: "Drayage",
            definition: "Short-distance transport of goods by truck, typically between ports, rail yards, and nearby warehouses or distribution centers.",
            category: "transportation",
            letter: "D"
        },
        {
            term: "ETA (Estimated Time of Arrival)",
            definition: "The expected date and time when a shipment or vehicle is anticipated to arrive at its destination.",
            category: "operations",
            letter: "E"
        },
        {
            term: "ETD (Estimated Time of Departure)",
            definition: "The expected date and time when a shipment or vehicle is scheduled to depart from its origin or a transit point.",
            category: "operations",
            letter: "E"
        },
        {
            term: "FOB (Free on Board)",
            definition: "A shipping term that determines at what point ownership and liability for goods transfer from seller to buyer during transportation.",
            category: "international",
            letter: "F"
        },
        {
            term: "Freight Broker",
            definition: "An intermediary that connects shippers with carriers, helping to arrange transportation services while handling negotiations and documentation.",
            category: "operations",
            letter: "F"
        },
        {
            term: "Freight Class",
            definition: "A standardized classification system (50-500) used to categorize LTL shipments based on density, stowability, handling, and liability factors.",
            category: "shipping",
            letter: "F"
        },
        {
            term: "Freight Forwarder",
            definition: "A company that organizes shipments for individuals or corporations to get goods from origin to destination, often handling international logistics.",
            category: "international",
            letter: "F"
        },
        {
            term: "FTL (Full Truckload)",
            definition: "A shipping method where the entire truck trailer is dedicated to one shipper's cargo, typically used for large shipments of 15,000+ pounds.",
            category: "transportation",
            letter: "F"
        },
        {
            term: "Gross Weight",
            definition: "The total weight of a shipment including the product, packaging, pallets, and any other materials used in shipping.",
            category: "shipping",
            letter: "G"
        },
        {
            term: "Harmonized System (HS) Code",
            definition: "An internationally standardized system of names and numbers used to classify traded products for customs and statistical purposes.",
            category: "international",
            letter: "H"
        },
        {
            term: "Incoterms",
            definition: "International Commercial Terms that define the responsibilities of buyers and sellers in international trade transactions, including risk transfer and cost allocation.",
            category: "international",
            letter: "I"
        },
        {
            term: "Intermodal Transportation",
            definition: "The use of two or more modes of transportation (truck, rail, ship, air) to move goods from origin to destination in the same container or trailer.",
            category: "transportation",
            letter: "I"
        },
        {
            term: "Just-in-Time (JIT)",
            definition: "An inventory management strategy where materials and products are delivered exactly when needed in production or sales, minimizing inventory costs.",
            category: "operations",
            letter: "J"
        },
        {
            term: "Last Mile Delivery",
            definition: "The final step of the delivery process from a distribution center or facility to the end customer's location, often the most expensive part of shipping.",
            category: "operations",
            letter: "L"
        },
        {
            term: "Less Than Truckload (LTL)",
            definition: "A shipping method for freight that doesn't require a full truck trailer, where multiple shippers' goods are consolidated into one truck.",
            category: "transportation",
            letter: "L"
        },
        {
            term: "Logistics",
            definition: "The detailed coordination and implementation of complex operations involving the movement, storage, and flow of goods, services, and information.",
            category: "operations",
            letter: "L"
        },
        {
            term: "Manifest",
            definition: "A document listing all cargo, passengers, or crew aboard a ship, aircraft, or vehicle, used for customs and regulatory purposes.",
            category: "documentation",
            letter: "M"
        },
        {
            term: "Net Weight",
            definition: "The weight of goods alone, excluding packaging, containers, pallets, or other shipping materials.",
            category: "shipping",
            letter: "N"
        },
        {
            term: "NVOCC (Non-Vessel Operating Common Carrier)",
            definition: "A company that provides ocean transportation services without owning vessels, instead contracting space from actual ship operators.",
            category: "international",
            letter: "N"
        },
        {
            term: "Ocean Freight",
            definition: "Transportation of goods by sea in containers or bulk, typically the most cost-effective method for international shipping of large quantities.",
            category: "international",
            letter: "O"
        },
        {
            term: "Pallet",
            definition: "A flat transport structure used as a base for assembling, storing, stacking, handling, and transporting goods, typically made of wood or plastic.",
            category: "shipping",
            letter: "P"
        },
        {
            term: "POD (Proof of Delivery)",
            definition: "Documentation that confirms successful delivery of goods to the consignee, often including signature, date, and time of delivery.",
            category: "documentation",
            letter: "P"
        },
        {
            term: "Port of Entry",
            definition: "An official location where goods and people can enter a country, featuring customs and immigration facilities for international trade.",
            category: "international",
            letter: "P"
        },
        {
            term: "Quarantine",
            definition: "The isolation and inspection of goods or vessels to prevent the spread of disease or pests, required for certain international shipments.",
            category: "international",
            letter: "Q"
        },
        {
            term: "Rail Freight",
            definition: "Transportation of goods by railroad, often used for long-distance shipping of bulk commodities and containers.",
            category: "transportation",
            letter: "R"
        },
        {
            term: "RFID (Radio Frequency Identification)",
            definition: "Technology that uses radio waves to identify and track tags attached to objects, widely used in supply chain management for inventory tracking.",
            category: "operations",
            letter: "R"
        },
        {
            term: "Supply Chain",
            definition: "The network of organizations, people, activities, information, and resources involved in moving a product or service from supplier to customer.",
            category: "operations",
            letter: "S"
        },
        {
            term: "TEU (Twenty-foot Equivalent Unit)",
            definition: "A standard measurement unit used to describe the capacity of container ships and container terminals, based on a 20-foot container.",
            category: "shipping",
            letter: "T"
        },
        {
            term: "Transit Time",
            definition: "The total time required for a shipment to travel from origin to destination, including any stops or transfers along the way.",
            category: "operations",
            letter: "T"
        },
        {
            term: "Truckload (TL)",
            definition: "A shipping method where an entire truck trailer is used for one shipment, typically for large quantities that fill most or all of the trailer space.",
            category: "transportation",
            letter: "T"
        },
        {
            term: "Warehouse Management System (WMS)",
            definition: "Software that manages warehouse operations including inventory tracking, order picking, receiving, and shipping processes.",
            category: "operations",
            letter: "W"
        },
        {
            term: "Yard Management",
            definition: "The supervision of vehicle and trailer movements within a facility's yard or dock area, optimizing efficiency and reducing wait times.",
            category: "operations",
            letter: "Y"
        }
    ];

    const filteredTerms = logisticsTerms.filter(term => {
        const matchesSearch = searchTerm === '' ||
            term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
            term.definition.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
        const matchesLetter = selectedLetter === 'all' || term.letter === selectedLetter;
        return matchesSearch && matchesCategory && matchesLetter;
    });

    const groupedTerms = filteredTerms.reduce((acc, term) => {
        const letter = term.letter;
        if (!acc[letter]) acc[letter] = [];
        acc[letter].push(term);
        return acc;
    }, {});

    const hasContent = Object.keys(groupedTerms).length > 0;

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "DefinedTermSet",
        "name": "Logistics Terms Glossary",
        "description": "Comprehensive glossary of logistics, transportation, and supply chain terminology",
        "url": "https://roadexpressfreight.com/glossary",
        "publisher": {
            "@type": "Organization",
            "name": "RoadExpress Freight"
        },
        "hasDefinedTerm": logisticsTerms.map(term => ({
            "@type": "DefinedTerm",
            "name": term.term,
            "description": term.definition
        }))
    };

    return (
        <>
            <Header />
            {/* <SEOHead
                title="Logistics Terms Glossary | Transportation & Supply Chain Dictionary | RoadExpress"
                description="Comprehensive glossary of logistics, transportation, and supply chain terms. Learn freight terminology, shipping definitions, and industry jargon with RoadExpress."
                keywords="logistics glossary, transportation terms, freight dictionary, supply chain terminology, shipping definitions, logistics vocabulary, freight terms"
                canonical="https://roadexpressfreight.com/glossary"
                structuredData={structuredData}
            /> */}

            <div ref={sectionRef} className="min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            className={`transform transition-all duration-1000 translate-y-0 opacity-100
                                }`}
                        >
                            <div className="flex items-center mb-6">
                                <BookOpen className="w-8 h-8 mr-4" />
                                <span className="text-green-100 font-semibold tracking-wider text-sm uppercase">
                                    Knowledge Base
                                </span>
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                Logistics Terms
                                <br />
                                <span className="text-green-200">Glossary</span>
                            </h1>
                            <p className="text-xl text-green-100 leading-relaxed max-w-3xl">
                                Master the language of logistics with our comprehensive dictionary of transportation,
                                shipping, and supply chain terminology.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Search and Filters */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                        <div className="grid lg:grid-cols-12 gap-6">
                            {/* Search Bar */}
                            <div className="lg:col-span-5">
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="text"
                                        placeholder="Search terms or definitions..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                    />
                                </div>
                            </div>

                            {/* Category Filter */}
                            <div className="lg:col-span-4">
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                >
                                    {categories.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Letter Filter */}
                            <div className="lg:col-span-3">
                                <select
                                    value={selectedLetter}
                                    onChange={(e) => setSelectedLetter(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                >
                                    <option value="all">All Letters</option>
                                    <option value="3">Numbers</option>
                                    {alphabet.map(letter => (
                                        <option key={letter} value={letter}>{letter}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{logisticsTerms.length}</div>
                                    <div className="text-sm text-gray-600">Total Terms</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{categories.length - 1}</div>
                                    <div className="text-sm text-gray-600">Categories</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{filteredTerms.length}</div>
                                    <div className="text-sm text-gray-600">Filtered Results</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{Object.keys(groupedTerms).length}</div>
                                    <div className="text-sm text-gray-600">Letter Groups</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-8">
                        {/* Sidebar - Categories */}
                        <div className="lg:col-span-3">
                            <div className="sticky top-8">
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                        <Filter className="w-5 h-5 mr-2 text-green-600" />
                                        Categories
                                    </h3>
                                    <div className="space-y-2">
                                        {categories.map(category => (
                                            <button
                                                key={category.id}
                                                onClick={() => setSelectedCategory(category.id)}
                                                className={`w-full flex items-center text-left p-3 rounded-xl transition-all duration-300 ${selectedCategory === category.id
                                                    ? 'bg-green-600 text-white'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <category.icon className="w-4 h-4 mr-3 flex-shrink-0" />
                                                <span className="text-sm font-medium">{category.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Alphabet Navigation */}
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Jump</h3>
                                    <div className="grid grid-cols-6 gap-2">
                                        <button
                                            onClick={() => setSelectedLetter('3')}
                                            className={`p-2 text-center rounded-lg font-medium transition-all ${selectedLetter === '3'
                                                ? 'bg-green-600 text-white'
                                                : 'text-gray-600 hover:bg-gray-100'
                                                }`}
                                        >
                                            #
                                        </button>
                                        {alphabet.map(letter => (
                                            <button
                                                key={letter}
                                                onClick={() => setSelectedLetter(letter)}
                                                className={`p-2 text-center rounded-lg font-medium transition-all ${selectedLetter === letter
                                                    ? 'bg-green-600 text-white'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                            >
                                                {letter}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Content - Terms */}
                        <div className="lg:col-span-9">
                            {Object.keys(groupedTerms).sort().map((letter, groupIndex) => (
                                <div key={letter} className="mb-8">
                                    <div className="flex items-center mb-6">
                                        <div className="w-12 h-12 bg-green-600 text-white rounded-xl flex items-center justify-center mr-4">
                                            <span className="text-xl font-bold">{letter === '3' ? '#' : letter}</span>
                                        </div>
                                        <h2 className="text-3xl font-bold text-gray-900">
                                            {letter === '3' ? 'Numbers' : `Letter ${letter}`}
                                        </h2>
                                    </div>

                                    <div className="space-y-4">
                                        {groupedTerms[letter].map((term, index) => (
                                            <div
                                                key={`${letter}-${index}`}
                                                className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transform transition-all duration-1000 hover:shadow-md translate-y-0 opacity-100
                                                    }`}
                                                style={{ transitionDelay: `${groupIndex * 0.1 + index * 0.05}s` }}
                                            >
                                                <div className="flex items-start justify-between mb-4">
                                                    <h3 className="text-xl font-bold text-gray-900">{term.term}</h3>
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${term.category === 'transportation' ? 'bg-blue-100 text-blue-800' :
                                                        term.category === 'shipping' ? 'bg-orange-100 text-orange-800' :
                                                            term.category === 'documentation' ? 'bg-purple-100 text-purple-800' :
                                                                term.category === 'international' ? 'bg-green-100 text-green-800' :
                                                                    'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {categories.find(c => c.id === term.category)?.name || 'General'}
                                                    </span>
                                                </div>
                                                <p className="text-gray-700 leading-relaxed">{term.definition}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {!hasContent && (
                                <div className="text-center py-16">
                                    <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No terms found</h3>
                                    <p className="text-gray-600">
                                        Try adjusting your search criteria or browse different categories.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Download Button */}
                {/* <div className="fixed bottom-8 left-8">
                    <button className="flex items-center bg-white text-green-600 px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200">
                        <Download className="w-4 h-4 mr-2" />
                        <span className="font-medium">Download PDF</span>
                    </button>
                </div> */}
            </div>
            <Footer />
        </>
    );
};

export default LogisticsTermsPage;